import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { SearchSection } from "./price-check/SearchSection";
import { SearchResults } from "./price-check/SearchResults";
import { usePriceCheckQueries } from "./price-check/hooks/usePriceCheckQueries";
import { useIsMobile } from "@/hooks/use-mobile";

interface PriceCheckProps {
  onBack: () => void;
}

interface ProductPrice {
  id: string;
  barcode: string | null;
  name: string;
  current_price: number | null;
  category: string | null;
  category_name: string | null;
  supplier: string | null;
  last_synced: string;
  square_item_id: string;
  description: string | null;
  inventory_tracking: boolean | null;
  current_stock: number | null;
}

export const PriceCheck = ({ onBack }: PriceCheckProps) => {
  const [barcode, setBarcode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const {
    searchResults,
    searchProducts,
    hasSearched
  } = usePriceCheckQueries(barcode, "search", 0);


  const handleSearchWithBarcode = async (searchBarcode: string) => {
    setIsLoading(true);
    setBarcode(searchBarcode);
    
    try {
      await searchProducts();
      // Auto-clear input after search for next scan
      setTimeout(() => {
        setBarcode("");
      }, 3000); // Clear after 3 seconds to show results first
    } catch (error) {
      console.error("Search failed:", error);
      // Clear input even on error for next scan
      setTimeout(() => {
        setBarcode("");
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (!barcode.trim()) {
      return;
    }

    handleSearchWithBarcode(barcode);
  };

  const handleClearSearch = () => {
    setBarcode("");
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container mx-auto p-4 md:p-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={onBack} size={isMobile ? "sm" : "default"} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            {!isMobile && "Back"}
          </Button>
          <div className="text-center flex-1">
            <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Price Check
            </h1>
            <p className="text-muted-foreground mt-2">Scan or enter a barcode to check retail prices</p>
          </div>
        </div>

        {/* Main Content - Centered Search */}
        <div className="max-w-2xl mx-auto">
          <SearchSection
            barcode={barcode}
            setBarcode={setBarcode}
            onSearch={handleSearch}
            onClearSearch={handleClearSearch}
            isLoading={isLoading}
          />
          <SearchResults results={searchResults} isLoading={isLoading} hasSearched={hasSearched} />
        </div>
      </div>
    </div>
  );
};
